import React, { useState } from "react";
import { MessageSquare, X, Send, Check, Sparkles, AlertCircle, Loader2 } from "lucide-react";

type ServiceType = "office" | "retail" | "strata" | "construction" | "residential" | "other";
type Frequency = "one-time" | "daily" | "weekly" | "bi-weekly" | "monthly";
type Size = "under-2k" | "2k-5k" | "5k-10k" | "over-10k";

interface ChatState {
  step: number;
  service: ServiceType | null;
  frequency: Frequency | null;
  size: Size | null;
  contact: string;
}

const SERVICES = [
  { id: "office" as ServiceType, label: "Commercial Office" },
  { id: "retail" as ServiceType, label: "Retail Space" },
  { id: "strata" as ServiceType, label: "Strata / Common Area" },
  { id: "construction" as ServiceType, label: "Post-Construction" },
  { id: "residential" as ServiceType, label: "Residential Space" },
  { id: "other" as ServiceType, label: "Other / Specialty" },
];

const FREQUENCIES = [
  { id: "one-time" as Frequency, label: "One-time / Deep Clean" },
  { id: "daily" as Frequency, label: "Daily Janitorial" },
  { id: "weekly" as Frequency, label: "Weekly Service" },
  { id: "bi-weekly" as Frequency, label: "Bi-weekly Service" },
  { id: "monthly" as Frequency, label: "Monthly Service" },
];

const SIZES = [
  { id: "under-2k" as Size, label: "Under 2,000 sq. ft." },
  { id: "2k-5k" as Size, label: "2,000 – 5,000 sq. ft." },
  { id: "5k-10k" as Size, label: "5,000 – 10,000 sq. ft." },
  { id: "over-10k" as Size, label: "Over 10,000 sq. ft." },
];

export function ChatBubble() {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<ChatState>({
    step: 1,
    service: null,
    frequency: null,
    size: null,
    contact: "",
  });
  const [contactError, setContactError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const resetChat = () => {
    setState({
      step: 1,
      service: null,
      frequency: null,
      size: null,
      contact: "",
    });
    setContactError("");
    setLoading(false);
    setSubmitError("");
  };

  const handleSelectService = (service: ServiceType) => {
    setState((prev) => ({ ...prev, service, step: 2 }));
  };

  const handleSelectFrequency = (frequency: Frequency) => {
    setState((prev) => ({ ...prev, frequency, step: 3 }));
  };

  const handleSelectSize = (size: Size) => {
    setState((prev) => ({ ...prev, size, step: 4 }));
  };

  const validateContact = (val: string) => {
    if (!val.trim()) {
      return "Please enter your email or phone number.";
    }
    // Simple verification for either email or phone
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s()+-]{7,15}$/;
    if (!emailRegex.test(val) && !phoneRegex.test(val)) {
      return "Please enter a valid email or phone number.";
    }
    return "";
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateContact(state.contact);
    if (error) {
      setContactError(error);
      return;
    }
    setContactError("");
    setLoading(true);
    setSubmitError("");

    try {
      const baseApiUrl = import.meta.env.VITE_API_URL 
        ? import.meta.env.VITE_API_URL.replace(/\/contact$/, "") 
        : "https://creative-confluence-hub.onrender.com/api";
      const chatApiUrl = `${baseApiUrl}/chat-quote`;

      const payload = {
        service: getServiceLabel() || state.service,
        frequency: getFrequencyLabel() || state.frequency,
        size: getSizeLabel() || state.size,
        contact: state.contact
      };

      const response = await fetch(chatApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Could not send inquiry.");
      }

      setState((prev) => ({ ...prev, step: 5 }));
    } catch (err: any) {
      setSubmitError(err.message || "Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to get selected names for summaries
  const getServiceLabel = () => SERVICES.find((s) => s.id === state.service)?.label;
  const getFrequencyLabel = () => FREQUENCIES.find((f) => f.id === state.frequency)?.label;
  const getSizeLabel = () => SIZES.find((sz) => sz.id === state.size)?.label;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* CHAT WIDGET BOX */}
      {isOpen && (
        <div className="mb-4 w-[350px] sm:w-[400px] h-[500px] bg-background/95 backdrop-blur-md rounded-2xl shadow-elevated border border-border flex flex-col overflow-hidden transition-all duration-300 animate-fade-up">
          {/* Header */}
          <div className="bg-navy p-4 flex items-center justify-between text-primary-foreground">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-mint/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-mint" />
              </span>
              <div>
                <h4 className="font-semibold text-sm">Aureline Assistant</h4>
                <p className="text-[10px] text-primary-foreground/70">Online • Typically replies instantly</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-primary-foreground/75 hover:text-primary-foreground transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 flex flex-col justify-end">
            {/* Question 1: Service Selection */}
            {state.step >= 1 && (
              <div className="flex flex-col gap-2 max-w-[85%]">
                <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-none p-3 text-sm">
                  Hello! What type of space do you need cleaning services for?
                </div>
              </div>
            )}

            {/* Answer 1 (User selection) */}
            {state.step > 1 && state.service && (
              <div className="flex flex-col items-end gap-2 self-end max-w-[85%]">
                <div className="bg-navy text-primary-foreground rounded-2xl rounded-tr-none p-3 text-sm font-medium">
                  {getServiceLabel()}
                </div>
              </div>
            )}

            {/* Question 2: Frequency */}
            {state.step >= 2 && (
              <div className="flex flex-col gap-2 max-w-[85%] animate-fade-in">
                <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-none p-3 text-sm">
                  Excellent choice. How frequently will you require these services?
                </div>
              </div>
            )}

            {/* Answer 2 */}
            {state.step > 2 && state.frequency && (
              <div className="flex flex-col items-end gap-2 self-end max-w-[85%]">
                <div className="bg-navy text-primary-foreground rounded-2xl rounded-tr-none p-3 text-sm font-medium">
                  {getFrequencyLabel()}
                </div>
              </div>
            )}

            {/* Question 3: Size */}
            {state.step >= 3 && (
              <div className="flex flex-col gap-2 max-w-[85%] animate-fade-in">
                <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-none p-3 text-sm">
                  Got it. Roughly what is the total square footage of the space?
                </div>
              </div>
            )}

            {/* Answer 3 */}
            {state.step > 3 && state.size && (
              <div className="flex flex-col items-end gap-2 self-end max-w-[85%]">
                <div className="bg-navy text-primary-foreground rounded-2xl rounded-tr-none p-3 text-sm font-medium">
                  {getSizeLabel()}
                </div>
              </div>
            )}

            {/* Question 4: Contact Info */}
            {state.step >= 4 && (
              <div className="flex flex-col gap-2 max-w-[85%] animate-fade-in">
                <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-none p-3 text-sm">
                  Perfect. Please provide your email or phone number, and our estimator will get back to you with a direct proposal.
                </div>
              </div>
            )}

            {/* Answer 4 / Success */}
            {state.step === 5 && (
              <>
                <div className="flex flex-col items-end gap-2 self-end max-w-[85%]">
                  <div className="bg-navy text-primary-foreground rounded-2xl rounded-tr-none p-3 text-sm font-medium">
                    {state.contact}
                  </div>
                </div>
                <div className="flex flex-col gap-2 max-w-[85%] animate-fade-in">
                  <div className="bg-mint/10 border border-mint/20 text-mint-ink rounded-2xl rounded-tl-none p-3 text-sm flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 text-mint-ink shrink-0" />
                    <span>
                      Thank you! We have received your request. A team member will reach out to you within 24 hours.
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Interactive Input/Options Area */}
          <div className="p-4 bg-muted/30 border-t border-border flex flex-col justify-center min-h-[120px]">
            {/* Step 1 Options */}
            {state.step === 1 && (
              <div className="grid grid-cols-2 gap-2 w-full animate-fade-in">
                {SERVICES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleSelectService(s.id)}
                    className="py-2 px-3 text-xs bg-background hover:bg-navy hover:text-white rounded-lg border border-border text-foreground transition text-left cursor-pointer truncate font-medium shadow-sm hover:border-navy"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Step 2 Options */}
            {state.step === 2 && (
              <div className="grid grid-cols-2 gap-2 w-full animate-fade-in">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => handleSelectFrequency(f.id)}
                    className="py-2 px-3 text-xs bg-background hover:bg-navy hover:text-white rounded-lg border border-border text-foreground transition text-left cursor-pointer truncate font-medium shadow-sm hover:border-navy"
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {/* Step 3 Options */}
            {state.step === 3 && (
              <div className="grid grid-cols-2 gap-2 w-full animate-fade-in">
                {SIZES.map((sz) => (
                  <button
                    key={sz.id}
                    onClick={() => handleSelectSize(sz.id)}
                    className="py-2 px-3 text-xs bg-background hover:bg-navy hover:text-white rounded-lg border border-border text-foreground transition text-left cursor-pointer truncate font-medium shadow-sm hover:border-navy"
                  >
                    {sz.label}
                  </button>
                ))}
              </div>
            )}

             {/* Step 4 Form */}
             {state.step === 4 && (
               <form onSubmit={handleContactSubmit} className="w-full space-y-2 animate-fade-in">
                 <div className="relative flex items-center">
                   <input
                     type="text"
                     value={state.contact}
                     disabled={loading}
                     onChange={(e) => {
                       setState((prev) => ({ ...prev, contact: e.target.value }));
                       if (contactError) setContactError("");
                       if (submitError) setSubmitError("");
                     }}
                     placeholder="Email or Phone Number"
                     className="w-full text-xs py-2.5 pl-3 pr-10 bg-background border border-border rounded-lg outline-none focus:border-navy transition disabled:opacity-50"
                     autoFocus
                   />
                   <button
                     type="submit"
                     disabled={loading}
                     className="absolute right-1 w-8 h-8 rounded-md bg-navy hover:bg-navy-soft text-white flex items-center justify-center cursor-pointer transition disabled:opacity-50"
                   >
                     {loading ? (
                       <Loader2 className="w-3.5 h-3.5 animate-spin" />
                     ) : (
                       <Send className="w-3.5 h-3.5" />
                     )}
                   </button>
                 </div>
                 {contactError && (
                   <div className="text-[10px] text-destructive flex items-center gap-1">
                     <AlertCircle className="w-3 h-3" />
                     <span>{contactError}</span>
                   </div>
                 )}
                 {submitError && (
                   <div className="text-[10px] text-destructive flex items-center gap-1">
                     <AlertCircle className="w-3 h-3" />
                     <span>{submitError}</span>
                   </div>
                 )}
               </form>
             )}

            {/* Success screen restart */}
            {state.step === 5 && (
              <div className="w-full text-center animate-fade-in">
                <button
                  onClick={resetChat}
                  className="px-4 py-2 text-xs bg-navy hover:bg-navy-soft text-white rounded-lg transition font-semibold cursor-pointer shadow-sm"
                >
                  Start New Inquiry
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FLOAT TOGGLE BUTTON - Shifted if chat box is closed and scroll-to-top is active */}
      <button
        onClick={() => {
          if (!isOpen) {
            resetChat();
          }
          setIsOpen(!isOpen);
        }}
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-elevated transition-all duration-300 active:scale-95 cursor-pointer text-white ${
          isOpen ? "bg-navy hover:bg-navy-soft" : "bg-mint hover:bg-mint-soft text-navy"
        }`}
        aria-label="Toggle chat assistant"
      >
        {isOpen ? (
          <X className="w-6 h-6 animate-fade-in" />
        ) : (
          <MessageSquare className="w-6 h-6 animate-fade-in text-navy-deep" />
        )}
      </button>
    </div>
  );
}
